import './PreLanding.sass';
import {
  createRef,
  FC,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IcDiamond, IcGlobe24 } from '$assets';
import { Button } from '$uikit';
import { classNames, importAllFromDirectory, statReachGoal } from '$utils';
import { useDispatch, useSelector } from 'react-redux';
import { profileActions } from '$store/profile';
import { AuthPopoverState, ImportedModule } from '$store/models';
import { AuthPopoverSimple } from '$core';
import { PreLandingProps } from './PreLanding.interface';
import { authSelector } from '$store/auth';
import { goTo } from '$navigation/helper';
import { FEED, SUBSCRIPTION_ADULT } from '$shared/constants/pages';
import { changeLanguage } from '$translation';
import { useCurrentLang } from '$hooks';

export const PreLanding: FC<PreLandingProps> = (props) => {
  const { content, className, action = 'auth', href = '' } = props;

  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const landingRef = useRef(null);
  const { isLogged } = useSelector(authSelector);
  const [isMobile, setIsMobile] = useState(false);
  const [landingHeight, setLandingHeight] = useState(0);
  const [landingWidth, setLandingWidth] = useState(0);
  const [itemsRefs, setItemsRefs] = useState<MutableRefObject<HTMLDivElement>[]>([]);
  const [itemsContainerOffset, setItemsContainerOffset] = useState(0);
  const [currentItem, setCurrentItem] = useState(0);
  const [isItemsAnimated, setIsItemsAnimated] = useState(true);
  const currentLang = useCurrentLang();
  const dispatch = useDispatch();

  const handleContentLoaded = () => {
    setIsContentLoaded(true);
  };

  useEffect(() => {
    statReachGoal('pre_landing');
    window.addEventListener('load', handleContentLoaded);

    const observer = new ResizeObserver((entries) => {
      setLandingHeight(entries[0].contentRect.height);
      setLandingWidth(entries[0].contentRect.width);
    });
    landingRef.current && observer.observe(landingRef.current);

    return () => {
      window.removeEventListener('load', handleContentLoaded);
      landingRef.current && observer.unobserve(landingRef.current);
    };
  }, []);

  const handleIsMobile = () => {
    setIsMobile(window.innerWidth <= 720);
  };

  useEffect(() => {
    window.addEventListener('resize', handleIsMobile);
    handleIsMobile();
    return () => {
      window.removeEventListener('resize', handleIsMobile);
    };
  }, []);

  useEffect(() => {
    isLogged && goTo(SUBSCRIPTION_ADULT);
  }, []);

  const extractImages = (imagesModules: ImportedModule) => {
    return Object.values(imagesModules).map((item) => item.default);
  };

  const images = useMemo(() => {
    const desktopImagesModules = importAllFromDirectory(
      require.context(
        '../../../assets/modelsLandingImages/desktop',
        false,
        /\.(png|jpe?g|svg)$/,
      ),
    );

    const mobileImagesModules = importAllFromDirectory(
      require.context(
        '../../../assets/modelsLandingImages/mobile',
        false,
        /\.(png|jpe?g|svg)$/,
      ),
    );

    return {
      desktop: extractImages(desktopImagesModules),
      mobile: extractImages(mobileImagesModules),
    };
  }, []);

  const handleItemClick = (itemInd: number) => {
    if (itemInd >= content.length - 1) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      statReachGoal(`pre_lang_complete`);
      return;
    }

    statReachGoal(`pre_lang_pass_${itemInd}`);
    setCurrentItem(itemInd + 1);
    setIsItemsAnimated(true);
    setItemsContainerOffset(
      itemsContainerOffset + itemsRefs[itemInd]?.current.offsetHeight + landingHeight,
    );
  };

  useEffect(() => {
    setItemsRefs((itemRefs) => {
      return Array(content.length)
        .fill(null)
        .map((_, i) => itemRefs[i] || createRef());
    });
  }, [content.length]);

  useEffect(() => {
    if (currentItem === 0) return;

    const itemsHeights = itemsRefs.slice(0, currentItem).reduce((sum, item) => {
      return sum + item.current?.offsetHeight + landingHeight;
    }, 0);

    setIsItemsAnimated(false);
    setItemsContainerOffset(itemsHeights);
  }, [landingHeight, landingWidth, currentLang.key]);

  return (
    <div
      className={classNames('PreLanding', className, {
        _isMobile: isMobile,
      })}
      ref={landingRef}
    >
      <div className="PreLanding__bg__wrapper">
        {Array(2)
          .fill(null)
          .map((_, ind) => (
            <div
              key={ind}
              className={`PreLanding__bg ${isContentLoaded ? '_animated' : ''}`}
              style={{ animationDuration: `${isMobile ? 200 : 150}s` }}
            >
              {images[isMobile ? 'mobile' : 'desktop'].map((src) => (
                <img className="PreLanding__bgItem" key={src} src={src} alt=""></img>
              ))}
            </div>
          ))}
      </div>
      <div className="PreLanding__header">
        <IcDiamond /> Ton.Place
      </div>
      <div
        className={`PreLanding__content ${isItemsAnimated ? '_animated' : ''}`}
        style={{
          transform: `translateY(${-itemsContainerOffset}px)`,
        }}
      >
        {content.map((item, ind) => {
          return (
            <div
              key={ind}
              className="PreLanding__content__item"
              style={{
                marginTop: ind === 0 ? 0 : `${landingHeight}px`,
              }}
              ref={itemsRefs[ind]}
            >
              {item.mainContent}
              <div className="PreLanding__content__item__buttons">
                {item.buttons.map((button, buttonInd) => {
                  if (action === 'link' && ind >= content.length - 1) {
                    return (
                      <Button
                        href={href}
                        key={buttonInd}
                        mode={button.mode}
                        size={`${isMobile ? 'big' : 'large'}`}
                      >
                        {button.text}
                      </Button>
                    );
                  }

                  return (
                    <Button
                      key={buttonInd}
                      mode={button.mode}
                      size={`${isMobile ? 'big' : 'large'}`}
                      onClick={() => handleItemClick(ind)}
                    >
                      {button.text}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="PreLanding__changeLangButton__wrap"
        onClick={() => changeLanguage(currentLang.key === 'en' ? 'ru' : 'en')}
      >
        <div className="PreLanding__changeLangButton">
          <IcGlobe24 /> {currentLang.key}
        </div>
      </div>
      <AuthPopoverSimple />
    </div>
  );
};
